import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import { connectSocket, onNewMessage, offNewMessage } from '../utils/socket';
import socket from '../utils/socket';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DotsThreeVertical, User, Users, WhatsappLogo, Phone, Globe, 
  MagnifyingGlass, X, Copy, Check, Link as LinkIcon, PhoneCall,
  ArrowLeft, Eye
} from '@phosphor-icons/react';
import ThemeToggleButton from '../components/ThemeToggleButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerSelect = ({ onChatSelect, isCompact = false, activeType = null }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { updateAppBadge, showNotification, playNotificationSound } = useWebNotification();
  const [activeTab, setActiveTab] = useState('chats');
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [handlers, setHandlers] = useState(() => {
    // Try to restore cached handlers from sessionStorage for smooth transitions
    try {
      const cached = sessionStorage.getItem('dws_handlers_cache');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalGroupUnread, setTotalGroupUnread] = useState(0);
  const [supportInfo, setSupportInfo] = useState({ phone: null, whatsapp: null, app_name: 'DWS Vault Chat' });
  const [showLinks, setShowLinks] = useState(false);
  const [customerLinks, setCustomerLinks] = useState([]);
  const [linksCopied, setLinksCopied] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [viewingStatus, setViewingStatus] = useState(null);
  const [statusProgress, setStatusProgress] = useState(0);
  const menuRef = useRef(null);
  const statusTimerRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const fetchUnreadRef = useRef(null);
  const unreadRequestRef = useRef(null);

  // Connect socket for real-time notifications & calls
  useEffect(() => {
    if (!user?.id) return;
    connectSocket(user.id);

    // Skip notifications for first 2 seconds after mount (prevents PWA re-open notification)
    let allowNotifications = false;
    const startTimer = setTimeout(() => { allowNotifications = true; }, 2000);

    // Listen for incoming calls even on main page
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user.id) {
        showNotification({ 
          title: 'Incoming Call', 
          body: `${data.caller_name || 'Someone'} is calling you`,
          tag: 'incoming-call'
        });
      }
    };

    // Listen for new messages - trigger notification + refresh unread
    const handleNewMessage = (data) => {
      // Only notify for messages from handlers/admins, NOT from customer themselves
      if (data.message && data.message.sender_id !== user.id && data.message.sender_role !== 'customer') {
        if (!isCompact && allowNotifications) {
          showNotification({ 
            title: 'New Message', 
            body: data.message.text || 'New message received',
            tag: `msg-${data.room_id}`
          });
        }
        // Immediately refresh unread counts
        if (fetchUnreadRef.current) fetchUnreadRef.current();
      }
    };

    socket.on('incoming_call', handleIncomingCall);
    onNewMessage(handleNewMessage);

    return () => {
      clearTimeout(startTimer);
      socket.off('incoming_call', handleIncomingCall);
      offNewMessage(handleNewMessage);
    };
  }, [user, isCompact, activeType]);

  // Fetch support info
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/settings?t=${Date.now()}`);
        const data = res.data || {};
        setSupportInfo({
          phone: data.support_phone || data.phone || '',
          whatsapp: data.whatsapp_link || data.whatsapp || '',
          telegram: data.telegram_link || '',
          instagram: data.instagram_link || '',
          app_name: data.app_name || 'DWS Vault Chat'
        });
      } catch (e) {}
    };
    fetch();
  }, []);

  // Fetch handlers list
  useEffect(() => {
    const fetchHandlers = async () => {
      try {
        const res = await axios.get(`${API}/users/all-staff`);
        const staffList = res.data.filter(u => u.role !== 'super_admin');
        setHandlers(staffList);
        sessionStorage.setItem('dws_handlers_cache', JSON.stringify(staffList));
      } catch (e) {}
    };
    fetchHandlers();
  }, []);

  // Fetch unread counts
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      if (unreadRequestRef.current) return unreadRequestRef.current;
      const request = (async () => {
      try {
        const response = await axios.get(`${API}/chat/unread-count`);
        const counts = {};
        (response.data?.unread_rooms || []).forEach((room) => {
          if (room.chat_type) counts[room.chat_type] = room.count;
        });
        setUnreadCounts(counts);
        // Update PWA badge
        const totalUnread = Object.values(counts).reduce((sum, c) => sum + c, 0);
        updateAppBadge(totalUnread);
      } catch (e) {}
      })();
      unreadRequestRef.current = request;
      try {
        return await request;
      } finally {
        if (unreadRequestRef.current === request) unreadRequestRef.current = null;
      }
    };
    fetchUnreadRef.current = fetchUnread;
    fetchUnread();
    const stopPoll = startPolling('customer-unread', fetchUnread, 5000);
    const cleanupVis = onVisibilityFetch(fetchUnread);
    return () => { stopPoll(); cleanupVis(); };
  }, [user]);

  // Fetch group unread
  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/groups/user-groups/unread-count?user_id=${user.id}`);
        setTotalGroupUnread(res.data.total_unread || 0);
      } catch (e) {}
    };
    fetch();
    const stopPoll = startPolling('customer-group-unread', fetch, 5000);
    return () => stopPoll();
  }, [user]);

  // Fetch statuses for Updates tab
  useEffect(() => {
    if (activeTab !== 'updates') return;
    const fetchStatuses = async () => {
      try {
        const res = await axios.get(`${API}/status/all`);
        setStatuses(res.data || []);
      } catch (e) {}
    };
    fetchStatuses();
  }, [activeTab]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Status auto-advance timer
  useEffect(() => {
    if (!viewingStatus) return;
    setStatusProgress(0);
    const startTime = Date.now();
    const duration = 10000; // 10 seconds
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setStatusProgress(progress);
      if (progress >= 100) {
        // Auto-advance to next status
        const currentIdx = statuses.findIndex(s => s.id === viewingStatus.id);
        if (currentIdx < statuses.length - 1) {
          setViewingStatus(statuses[currentIdx + 1]);
        } else {
          setViewingStatus(null);
        }
        return;
      }
      statusTimerRef.current = requestAnimationFrame(tick);
    };
    statusTimerRef.current = requestAnimationFrame(tick);

    // Record view
    if (user?.id) {
      axios.post(`${API}/status/${viewingStatus.id}/view`, { viewer_id: user.id, viewer_name: user.username }).catch(() => {});
    }

    return () => { if (statusTimerRef.current) cancelAnimationFrame(statusTimerRef.current); };
  }, [viewingStatus]);

  const handleSelect = (type) => {
    if (onChatSelect) onChatSelect(type);
    else navigate(`/customer-chat?type=${type}`);
  };

  // Search messages across all chats
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || !user?.id) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API}/chat/search-messages`, { params: { user_id: user.id, query: searchQuery } });
        setSearchResults(res.data.results || []);
      } catch (e) { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user]);

  const getHandlerForType = (type) => {
    const roleMap = { deposit: 'deposit_handler', withdrawal: 'withdrawal_handler', support: 'support_handler' };
    return handlers.find(h => h.role === roleMap[type]);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API}${url}`;
  };

  const getTimeAgo = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const isPWA = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const isSplitScreen = !!onChatSelect && !isPWA && window.innerWidth >= 1024;

  return (
    <div className={`${isSplitScreen ? 'h-full' : 'min-h-screen'} bg-wa-panel flex flex-col`} 
      style={!isSplitScreen ? { position: 'fixed', inset: 0 } : {}}>
      
      {/* Header - WhatsApp Style */}
      <div className="bg-[#008069] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-8 h-8 rounded-full" />
            <div>
              <h1 className="text-lg font-bold">DWS Vault</h1>
              <p className="text-[10px] text-white/70">Chat</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
          <ThemeToggleButton />
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-white/10 rounded-full" data-testid="customer-menu-btn">
              <DotsThreeVertical size={22} weight="bold" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-56 bg-wa-panel rounded-lg shadow-xl border border-wa-border z-50 py-1">
                <button onClick={() => { setShowMenu(false); navigate('/customer-dashboard'); }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3">
                  <User size={20} className="text-wa-text-secondary" />
                  <div><p className="text-sm font-medium text-wa-text-primary">Profile</p><p className="text-[10px] text-wa-text-secondary">View your profile</p></div>
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/customer/groups'); }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3">
                  <Users size={20} className="text-wa-text-secondary" />
                  <div><p className="text-sm font-medium text-wa-text-primary">Group Chat</p><p className="text-[10px] text-wa-text-secondary">Join group chats</p></div>
                </button>
                <button onClick={() => {
                  setShowMenu(false);
                  const wa = supportInfo.whatsapp;
                  if (wa) {
                    const url = wa.startsWith('http') ? wa : `https://wa.me/${wa.replace(/\D/g, '')}`;
                    window.open(url, '_blank');
                  } else {
                    toast.info('WhatsApp support not configured');
                  }
                }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3">
                  <WhatsappLogo size={20} className="text-green-600" />
                  <div><p className="text-sm font-medium text-wa-text-primary">WhatsApp Support</p><p className="text-[10px] text-wa-text-secondary">Chat on WhatsApp</p></div>
                </button>
                <button onClick={() => { setShowMenu(false); if (supportInfo.phone) window.location.href = `tel:${supportInfo.phone}`; else toast.info('Call support not configured'); }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3">
                  <Phone size={20} className="text-blue-600" />
                  <div><p className="text-sm font-medium text-wa-text-primary">Call Support</p><p className="text-[10px] text-wa-text-secondary">{supportInfo.phone || 'Not configured'}</p></div>
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Tabs: Chats / Updates */}
      <div className="bg-[#008069] flex flex-shrink-0">
        <button onClick={() => setActiveTab('chats')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${activeTab === 'chats' ? 'text-white border-b-2 border-white' : 'text-white/60'}`}>
          Chats
        </button>
        <button onClick={() => setActiveTab('updates')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${activeTab === 'updates' ? 'text-white border-b-2 border-white' : 'text-white/60'}`}>
          Updates
        </button>
      </div>

      {activeTab === 'chats' ? (
        <>
          {/* Search */}
          <div className="px-3 py-2 bg-wa-hover flex-shrink-0">
            <div className="flex items-center bg-wa-panel rounded-lg px-3 py-2 border border-wa-border">
              <MagnifyingGlass size={18} className="text-wa-text-secondary mr-2" />
              <input type="text" placeholder="Search chat..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400" data-testid="customer-search" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="p-0.5"><X size={16} className="text-wa-text-secondary" /></button>}
            </div>
          </div>

          {/* Search Results */}
          {searchQuery.length >= 2 ? (
            <div className="flex-1 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#008069] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-wa-text-secondary text-sm py-8">No messages found for "{searchQuery}"</p>
              ) : (
                searchResults.map((result, idx) => (
                  <div key={idx} onClick={() => { setSearchQuery(''); handleSelect(result.chat_type); }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border">
                    <div className="w-10 h-10 rounded-full bg-[#008069] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{(result.sender_name || 'U')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-wa-text-primary text-sm">{result.sender_name}</span>
                        <span className="text-[10px] text-wa-text-secondary capitalize">{result.chat_type}</span>
                      </div>
                      <p className="text-xs text-wa-text-secondary truncate mt-0.5">{result.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
          /* Chat List */
          <div className="flex-1 overflow-y-auto">
            {/* Deposit Handler */}
            {(() => {
              const h = getHandlerForType('deposit');
              const name = h?.username || 'Deposit';
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('deposit')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border ${activeType === 'deposit' ? 'bg-wa-active' : ''}`}
                  data-testid="deposit-chat-item">
                  <div className="w-12 h-12 rounded-full bg-[#00a884] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {h?.profile_pic ? <img src={getImageUrl(h.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-wa-text-primary text-sm">{name}</span>
                      <span className="text-[10px] text-wa-text-secondary">Deposit</span>
                    </div>
                    <p className="text-xs text-wa-text-secondary truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.deposit || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.deposit}</div>
                  )}
                </div>
              );
            })()}

            {/* Withdrawal Handler */}
            {(() => {
              const h = getHandlerForType('withdrawal');
              const name = h?.username || 'Withdrawal';
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('withdrawal')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border ${activeType === 'withdrawal' ? 'bg-wa-active' : ''}`}
                  data-testid="withdrawal-chat-item">
                  <div className="w-12 h-12 rounded-full bg-[#5856d6] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {h?.profile_pic ? <img src={getImageUrl(h.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-wa-text-primary text-sm">{name}</span>
                      <span className="text-[10px] text-wa-text-secondary">Withdrawal</span>
                    </div>
                    <p className="text-xs text-wa-text-secondary truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.withdrawal || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.withdrawal}</div>
                  )}
                </div>
              );
            })()}

            {/* Support Handler */}
            {(() => {
              const h = getHandlerForType('support');
              const name = h?.username || 'Support';
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('support')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border ${activeType === 'support' ? 'bg-wa-active' : ''}`}
                  data-testid="support-chat-item">
                  {/* Deepened from #ff9500, which only gave white text a 2.2:1
                      contrast ratio. Keeps the orange identity for Support. */}
                  <div className="w-12 h-12 rounded-full bg-[#d97706] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {h?.profile_pic ? <img src={getImageUrl(h.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-wa-text-primary text-sm">{name}</span>
                      <span className="text-[10px] text-wa-text-secondary">Support</span>
                    </div>
                    <p className="text-xs text-wa-text-secondary truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.support || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.support}</div>
                  )}
                </div>
              );
            })()}

            {/* Groups */}
            <div onClick={() => navigate('/customer/groups')}
              className="flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border" data-testid="groups-item">
              <div className="w-12 h-12 rounded-full bg-[#4ade80] flex items-center justify-center flex-shrink-0">
                <Users size={24} className="text-white" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-wa-text-primary text-sm">Groups</span>
                <p className="text-xs text-wa-text-secondary mt-0.5">Your group chats</p>
              </div>
              {totalGroupUnread > 0 && (
                <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{totalGroupUnread}</div>
              )}
            </div>

            {/* Links */}
            <div onClick={async () => {
              try { const res = await axios.get(`${API}/links`); setCustomerLinks(res.data || []); setShowLinks(true); } catch (e) {}
            }} className="flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border" data-testid="links-item">
              <div className="w-12 h-12 rounded-full bg-[#3b82f6] flex items-center justify-center flex-shrink-0">
                <LinkIcon size={24} className="text-white" weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-wa-text-primary text-sm">Links</span>
                <p className="text-xs text-wa-text-secondary mt-0.5">Quick links</p>
              </div>
            </div>

            {/* Call Support */}
            {supportInfo.phone && (
              <div onClick={() => window.location.href = `tel:${supportInfo.phone}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border" data-testid="call-support-item">
                <div className="w-12 h-12 rounded-full bg-[#06b6d4] flex items-center justify-center flex-shrink-0">
                  <PhoneCall size={24} className="text-white" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-wa-text-primary text-sm">Call Support</span>
                  <p className="text-xs text-wa-text-secondary mt-0.5">{supportInfo.phone}</p>
                </div>
              </div>
            )}
          </div>
          )}
        </>
      ) : (
        /* Updates Tab */
        <div className="flex-1 overflow-y-auto bg-wa-hover">
          <div className="p-4">
            <h3 className="text-lg font-bold text-wa-text-primary mb-3">Status</h3>
            {statuses.length === 0 ? (
              <p className="text-center text-wa-text-secondary text-sm py-8">No status updates</p>
            ) : (
              <div className="space-y-1 bg-wa-panel rounded-xl overflow-hidden shadow-sm">
                {statuses.map((status, idx) => (
                  <div key={status.id || idx} onClick={() => setViewingStatus(status)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border last:border-0">
                    <div className="w-12 h-12 rounded-full ring-2 ring-[#008069] ring-offset-2 overflow-hidden bg-[#008069] flex items-center justify-center flex-shrink-0">
                      {status.type === 'image' && status.image_url ?
                        <img src={getImageUrl(status.image_url)} alt="" className="w-full h-full object-cover" /> :
                        <span className="text-white font-bold">{(status.user_name || 'D')[0].toUpperCase()}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-wa-text-primary text-sm">DWS Team</span>
                      <p className="text-xs text-wa-text-secondary mt-0.5 truncate">
                        {status.type === 'text' ? status.content : (status.caption || (status.type === 'audio' ? 'Audio' : 'Photo'))} · {getTimeAgo(status.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Fullscreen Viewer */}
      {viewingStatus && (
        <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="customer-status-viewer">
          {/* Progress Bar */}
          <div className="h-1 bg-white/20 flex-shrink-0">
            <div className="h-full bg-wa-panel transition-all duration-100" style={{ width: `${statusProgress}%` }} />
          </div>
          {/* Header */}
          <div className="flex items-center gap-3 p-3 bg-black/50 flex-shrink-0">
            <button onClick={() => setViewingStatus(null)} className="text-white p-1"><ArrowLeft size={24} weight="bold" /></button>
            <div className="w-8 h-8 rounded-full bg-[#008069] flex items-center justify-center">
              <span className="text-white font-bold text-sm">D</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">DWS Team</p>
              <p className="text-white/60 text-xs">{getTimeAgo(viewingStatus.created_at)}</p>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-4">
            {viewingStatus.type === 'image' && viewingStatus.image_url ?
              <img src={getImageUrl(viewingStatus.image_url)} alt="" className="max-w-full max-h-full object-contain" /> :
              viewingStatus.type === 'audio' && viewingStatus.audio_url ?
                <div className="bg-white/10 rounded-2xl p-8 flex flex-col items-center gap-4">
                  <audio src={getImageUrl(viewingStatus.audio_url)} controls autoPlay className="w-72" />
                </div> :
                <div className="bg-[#008069] rounded-2xl p-8 max-w-md">
                  <p className="text-white text-xl text-center font-medium">{viewingStatus.content}</p>
                </div>}
          </div>
          {viewingStatus.caption && (
            <div className="p-4 bg-black/60 flex-shrink-0"><p className="text-white text-center text-sm">{viewingStatus.caption}</p></div>
          )}
        </div>
      )}

      {/* Links Modal */}
      {showLinks && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50" onClick={() => setShowLinks(false)}>
          <div className="bg-wa-panel w-full sm:w-[420px] sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-[#008069] text-white px-5 py-4 sm:rounded-t-2xl rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3"><Globe size={24} /><h2 className="text-lg font-bold">Links</h2></div>
              <button onClick={() => setShowLinks(false)} className="p-1.5 hover:bg-white/20 rounded-full"><X size={22} weight="bold" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {customerLinks.length === 0 ? (
                <p className="text-center py-10 text-wa-text-secondary text-sm">No links available</p>
              ) : customerLinks.map(link => (
                <div key={link.id} className="bg-wa-hover rounded-xl p-4 border border-wa-border">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-wa-text-primary text-sm mb-1">{link.description}</h3>
                      <a href={link.url} onClick={(e) => { e.preventDefault(); window.open(link.url, '_blank', 'noopener,noreferrer'); }} className="text-[#008069] text-xs break-all hover:underline">{link.url}</a>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(link.url); setLinksCopied(link.id); setTimeout(() => setLinksCopied(null), 2000); }}
                      className="p-2 hover:bg-wa-hover rounded-full flex-shrink-0">
                      {linksCopied === link.id ? <Check size={18} className="text-[#008069]" /> : <Copy size={18} className="text-wa-text-secondary" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelect;
